import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Skeleton, Tooltip } from "@mui/material";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import PaymentsIcon from "@mui/icons-material/Payments";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import styles from "./SideBar.module.scss";
import TestImage from "../static/logo-default.png";
import DarkTestImage from "../static/logo-dark.png";
import { UserContext } from "../Contexts/LoginContext";
import { RoleContext } from "../Contexts/RoleContext";
import { useAppModule } from "../Contexts/AppContext";
import { url } from "./url";
import { fetchWithAuth } from "./FetchWithAuth";
import NotificationBell from "../Components/notifications/NotificationBell";
import FlashAssistant from "./chat/FlashAssistant";
import { FlashContext } from "../Contexts/FlashContext";
import { ThemeContext } from "../Contexts/ThemeContext";
import ThemeSwitcher from "./ThemeSwitcher";
import { useBonusBar } from "../context/BonusBarContext";
import { getComposedNavTree } from "../navigation/appMenus";
import { pathMatches } from "../utils/pathMatches";
import { getDefaultRouteForRole } from "../utils/postLoginRoute";
import AppLauncher from "./appLauncher/AppLauncher";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import SidebarOnlineStaff from "./sidebar/SidebarOnlineStaff";
import { canViewOnlineStaffPresence } from "../utils/onlinePresenceAccess";

const BONUS_BAR_SIDEBAR_ROLES = [
  "admin",
  "Manager",
  "Counsellor",
  "Visa Counsellor",
  "Visa&Application Counsellor",
];

function SideBar() {
  const [collapsed, setCollapsed] = useState(false);
  const { isOpen: flashOpen } = useContext(FlashContext);
  const { themeMode } = useContext(ThemeContext);
  const effectiveCollapsed = collapsed || flashOpen;
  const [details, setDetails] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const launcherAnchorRef = useRef(null);
  const api_url = url;
  const { username } = useContext(UserContext);
  const { role, setRole } = useContext(RoleContext);
  const { currentApp, setAppLauncherOpen, appLauncherOpen, navOptions } = useAppModule();

  const navTree = useMemo(() => getComposedNavTree(currentApp, role, navOptions), [currentApp, role, navOptions]);

  /** Main menu scrolls; Tasks, Approvals, and Apps launcher stay fixed above the profile block. */
  const { navMain, navPinned, navPinnedWorkspace } = useMemo(() => {
    const main = [];
    let tasks = null;
    let approvals = null;
    let launcher = null;
    for (const entry of navTree) {
      if (entry.launcher) launcher = entry;
      else if (entry.path === "/tasks") tasks = entry;
      else if (entry.path === "/approvals") approvals = entry;
      else main.push(entry);
    }
    const pinned = [launcher, tasks, approvals].filter(Boolean);
    const launcherOnly = launcher ? [launcher] : [];
    return { navMain: main, navPinned: pinned, navPinnedWorkspace: launcherOnly };
  }, [navTree]);

  const handleLogout = async () => {
    try {
      await fetchWithAuth(`${api_url}/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
    } catch (error) {
      console.error("Error logging out:", error);
    }
    localStorage.clear();
    setRole("");
    navigate("/");
  };

  const fetchDetails = async () => {
    if (username) {
      try {
        const response = await fetchWithAuth(`${api_url}/get_user/${username}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (!response.ok) throw new Error("Unauthorized or failed request");
        const data = await response.json();
        setDetails(data);
        setRole(data.role);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
  };

  useEffect(() => {
    if (username) {
      fetchDetails();
    } else {
      handleLogout();
    }
  }, []);

  const { bonusBarDismissed, toggleBonusBar } = useBonusBar();
  const showBonusBarTrigger = BONUS_BAR_SIDEBAR_ROLES.includes(role);
  /** Floating AI launcher (abyss orb) — admin only for now. */
  const canUseAiChatbot = role === "admin";
  const showOnlineStaffPresence = canViewOnlineStaffPresence(details.role ?? role);

  useEffect(() => {
    const sidebarNode = sidebarRef.current;
    if (!sidebarNode) return undefined;
    const hostColumn = sidebarNode.closest("[class*='col-'], .sidebar-column, .sidebarGrid");
    if (!hostColumn) return undefined;
    hostColumn.classList.add("app-sidebar-host");
    const mainHost = hostColumn.nextElementSibling;
    if (mainHost) mainHost.classList.add("app-main-host");
    return () => {
      hostColumn.classList.remove("app-sidebar-host");
      if (mainHost) mainHost.classList.remove("app-main-host");
    };
  }, []);

  useEffect(() => {
    const sidebarWidth = effectiveCollapsed ? "88px" : "280px";
    document.documentElement.style.setProperty("--app-sidebar-width", sidebarWidth);
  }, [effectiveCollapsed]);

  const routeIsActive = (routePath) => pathMatches(routePath, location.pathname);

  const hasActiveSubItem = (subItems = []) => subItems.some((subItem) => routeIsActive(subItem.path));

  useEffect(() => {
    navMain.forEach((entry, key) => {
      if (!entry.group || !entry.children) return;
      const childHit = entry.children.some((ch) => routeIsActive(ch.path));
      if (childHit) setOpenDropdown(key);
    });
  }, [location.pathname, navMain]);

  const handleToggleItem = (item, key) => {
    if (item.launcher) {
      setAppLauncherOpen(true);
      return;
    }
    if (item.group) {
      setOpenDropdown(openDropdown === key ? null : key);
      return;
    }
    if (item.path) navigate(item.path);
  };

  const handleProfileClick = () => {
    setProfileMenuOpen((prev) => !prev);
  };

  const handleProfileMenuItem = (type) => {
    setProfileMenuOpen(false);
    if (type === "profile") {
      navigate("/userProfile");
      return;
    }
    if (type === "settings") {
      navigate("/userProfile?tab=settings");
      return;
    }
    if (type === "logout") {
      handleLogout();
    }
  };

  const goHome = () => navigate(getDefaultRouteForRole(role));

  const handleBrandWrapClick = (e) => {
    if (e.target.closest("[data-app-launcher]")) return;
    goHome();
  };

  return (
    <aside ref={sidebarRef} className={`${styles.Sidebar} ${effectiveCollapsed ? styles.collapsed : ""}`}>
      <div className={styles.sidebarContainer}>
        <div className={styles.SidebarHeader}>
          <div
            className={styles.brandWrap}
            role="button"
            tabIndex={0}
            onClick={handleBrandWrapClick}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              handleBrandWrapClick(e);
            }}
          >
            <div className={styles.brandLogoZone}>
              <div className={styles.brandLogo}>
                <img src={themeMode === "dark" ? DarkTestImage : TestImage} alt="Logo" className={styles.brandLogoImage} key={themeMode} />
              </div>
              <button
                type="button"
                ref={launcherAnchorRef}
                data-app-launcher
                className={styles.appLauncherBtn}
                aria-label="Switch app"
                aria-haspopup="dialog"
                onClick={(e) => {
                  e.stopPropagation();
                  setAppLauncherOpen(true);
                }}
              >
                <GridViewRoundedIcon className={styles.appLauncherGridIcon} />
              </button>
            </div>
            {!effectiveCollapsed && (
              <div className={styles.brandText}>
                <h2>CrunchTime</h2>
                <p>Built to get the job done</p>
              </div>
            )}
          </div>
          <button
            type="button"
            className={styles.SidebarToggle}
            onClick={() => setCollapsed(!collapsed)}
            aria-label={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {effectiveCollapsed ? (
              <KeyboardDoubleArrowRightIcon fontSize="small" />
            ) : (
              <KeyboardDoubleArrowLeftIcon fontSize="small" />
            )}
          </button>
        </div>

        <AppLauncher open={appLauncherOpen} anchorEl={launcherAnchorRef.current} onClose={() => setAppLauncherOpen(false)} />

        <div className={`${styles.themeAndNotificationsRow} ${effectiveCollapsed ? styles.rowCollapsed : ""}`}>
          {!effectiveCollapsed && (
            <div className={styles.headerControlSlot}>
              <ThemeSwitcher collapsed={effectiveCollapsed} />
            </div>
          )}
          {!effectiveCollapsed && showBonusBarTrigger && (
            <div className={styles.headerControlSlot}>
              <Tooltip title={bonusBarDismissed ? "Show bonus bar" : "Hide bonus bar"} arrow placement="right">
                <span className={styles.bonusBarTooltipAnchor}>
                  <button
                    type="button"
                    className={`${styles.bonusBarTrigger} ${
                      bonusBarDismissed ? styles.bonusBarTriggerInactive : styles.bonusBarTriggerActive
                    }`}
                    onClick={toggleBonusBar}
                    aria-label={bonusBarDismissed ? "Show bonus and rewards bar" : "Hide bonus and rewards bar"}
                    aria-pressed={!bonusBarDismissed}
                  >
                    <span className={styles.bonusBarTriggerInner}>
                      <PaymentsIcon />
                      {!effectiveCollapsed && <span className={styles.bonusBarMicroLabel}>Incentives</span>}
                    </span>
                  </button>
                </span>
              </Tooltip>
            </div>
          )}
          <div className={`${styles.headerControlSlot} ${styles.notificationBell}`}>
            <NotificationBell collapsed={effectiveCollapsed} />
          </div>
        </div>
      </div>

      <ul className={styles.SidebarList}>
        {!navMain.length && !navPinned.length && !effectiveCollapsed && (
          <li className={styles.row}>
            <div className={styles.title} style={{ opacity: 0.75 }}>
              No pages for your role in this app.
            </div>
          </li>
        )}
        {navMain.map((item, key) => {
          const isItemActive = item.path
            ? routeIsActive(item.path)
            : item.group
              ? hasActiveSubItem(item.children)
              : false;

          return (
            <React.Fragment key={`${item.label || item.path || "g"}-${key}`}>
              <li
                className={`${styles.row} ${isItemActive ? styles.active : ""}`}
                onClick={() => handleToggleItem(item, key)}
              >
                <div className={styles.icon}>{item.icon}</div>
                {!effectiveCollapsed && <div className={styles.title}>{item.label}</div>}
                {!effectiveCollapsed && item.group && (
                  <div className={styles.menuArrow}>{openDropdown === key ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
                )}
              </li>

              {!effectiveCollapsed && item.group && openDropdown === key && item.children && (
                <ul className={styles.SidebarSubList}>
                  {item.children.map((sub, subKey) => (
                    <li
                      key={subKey}
                      className={`${styles.row} ${styles.subRow} ${routeIsActive(sub.path) ? styles.active : ""}`}
                      onClick={() => navigate(sub.path)}
                    >
                      <div className={styles.title}>{sub.label}</div>
                    </li>
                  ))}
                </ul>
              )}
            </React.Fragment>
          );
        })}
      </ul>

      {navPinned.length > 0 && (
        <nav className={styles.SidebarPinnedNav} aria-label="Workspace">
          <button
            type="button"
            className={styles.workspacePanelToggle}
            onClick={() => setWorkspaceOpen((o) => !o)}
            aria-expanded={workspaceOpen}
            aria-controls="sidebar-workspace-items"
            id="sidebar-workspace-toggle"
            title={
              workspaceOpen
                ? "Hide Tasks and Approvals (Apps stays available)"
                : "Show Tasks and Approvals in workspace"
            }
          >
            {!effectiveCollapsed && <span className={styles.workspacePanelToggleLabel}>Workspace</span>}
            <span className={styles.workspacePanelToggleCaret} aria-hidden>
              {workspaceOpen ? (
                <KeyboardArrowDownIcon fontSize="small" />
              ) : (
                <KeyboardArrowRightIcon fontSize="small" />
              )}
            </span>
          </button>
          {(workspaceOpen ? navPinned : navPinnedWorkspace).length > 0 && (
            <ul id="sidebar-workspace-items" className={styles.pinnedQuickList}>
              {(workspaceOpen ? navPinned : navPinnedWorkspace).map((item, key) => {
                const isLauncher = Boolean(item.launcher);
                const isTasks = item.path === "/tasks";
                const isApprovals = item.path === "/approvals";
                const isItemActive = isLauncher ? appLauncherOpen : routeIsActive(item.path);
                const variant = isLauncher
                  ? styles.pinnedQuickApps
                  : isTasks
                    ? styles.pinnedQuickTasks
                    : isApprovals
                      ? styles.pinnedQuickApprovals
                      : styles.pinnedQuickTasks;
                return (
                  <li key={`pin-${item.path || "apps"}-${key}`} className={styles.pinnedQuickLi}>
                    <button
                      type="button"
                      className={`${styles.pinnedQuickItem} ${variant} ${isItemActive ? styles.pinnedQuickItemActive : ""}`}
                      onClick={() => handleToggleItem(item, key)}
                      title={effectiveCollapsed ? item.label : undefined}
                      aria-current={!isLauncher && isItemActive ? "page" : undefined}
                      aria-pressed={isLauncher ? appLauncherOpen : undefined}
                    >
                      <span className={styles.pinnedQuickIconWrap} aria-hidden>
                        {item.icon}
                      </span>
                      {!effectiveCollapsed && (
                        <span className={styles.pinnedQuickLabel}>
                          <span className={styles.pinnedQuickTitle}>{item.label}</span>
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>
      )}

      <div className={styles.SidebarFooter}>
        {!effectiveCollapsed && profileMenuOpen && (
          <div className={styles.profileDropdown}>
            <button type="button" className={`${styles.dropdownItem} ${styles.logoutItem}`} onClick={() => handleProfileMenuItem("logout")}>
              <LogoutRoundedIcon />
              <span>Logout</span>
            </button>
          </div>
        )}
        <div className={styles.profileFooterRow}>
          {!effectiveCollapsed && showOnlineStaffPresence && (
            <div className={styles.profilePresenceSlot}>
              <SidebarOnlineStaff apiUrl={api_url} currentUsername={details.username || username} themeMode={themeMode} />
            </div>
          )}
          <button
            type="button"
            className={`${styles.profileSection} ${styles.profileSectionMain} ${profileMenuOpen ? styles.profileSectionActive : ""}`}
            onClick={handleProfileClick}
          >
            <img src={themeMode === "dark" ? DarkTestImage : TestImage} alt="Profile" className={styles.profileAvatar} key={`profile-${themeMode}`} />
            {!effectiveCollapsed && (
              <>
                <div className={styles.profileInfo}>
                  {details.username ? (
                    <span className={styles.profileName}>{details.username}</span>
                  ) : (
                    <Skeleton className={styles.profileName} />
                  )}
                  {details.role ? (
                    <span className={styles.profileRole}>{details.role}</span>
                  ) : (
                    <Skeleton className={styles.profileRole} />
                  )}
                </div>
                <div className={styles.menuArrow}>{profileMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
              </>
            )}
          </button>
        </div>
      </div>
      {canUseAiChatbot && <FlashAssistant />}
    </aside>
  );
}

export default SideBar;
